import { Box, HStack, Text, Icon, useColorModeValue } from '@chakra-ui/react'
import { Database } from 'lucide-react'
import { formatBytes } from '@core/utils/format'
import { calculateUsagePercentage, getUsageStatus } from '@/lib/account/tier-permissions'

interface UsageBarProps {
  /** Current value */
  current: number
  /** Maximum value */
  max: number
  /** Label to display */
  label: string
  /** Lucide icon component */
  icon: React.ElementType
  /** Format function for values (defaults to toLocaleString) */
  formatValue?: (value: number) => string
}

/**
 * A progress bar component for displaying usage statistics.
 * Shows current/max values with a visual progress indicator.
 *
 * @example
 * <UsageBar current={5} max={10} label="Shares" icon={FileText} />
 */
export function UsageBar({
  current,
  max,
  label,
  icon,
  formatValue = (v) => v.toLocaleString(),
}: UsageBarProps) {
  const status = getUsageStatus(current, max)
  const isUnlimited = status === 'unlimited'
  const percentage = calculateUsagePercentage(current, max)
  const isWarning = status === 'warning'
  const isExceeded = status === 'exceeded'

  const labelColor = useColorModeValue('stone.600', 'stone.400')
  const iconColor = useColorModeValue('stone.500', 'stone.400')
  const trackBg = useColorModeValue('stone.200', 'stone.600')

  return (
    <Box>
      <HStack justify="space-between" mb={1}>
        <HStack spacing={2}>
          <Icon as={icon} boxSize={3} color={iconColor} />
          <Text fontSize="sm" color={labelColor}>
            {label}
          </Text>
        </HStack>
        <Text fontSize="sm" fontWeight="medium">
          {formatValue(current)} / {isUnlimited ? '∞' : formatValue(max)}
        </Text>
      </HStack>
      <Box h="8px" bg={trackBg} borderRadius="full" overflow="hidden">
        <Box
          h="full"
          w={`${percentage}%`}
          bg={isExceeded ? 'red.500' : isWarning ? 'orange.400' : 'brand.500'}
          borderRadius="full"
          transition="width 0.3s ease"
        />
      </Box>
    </Box>
  )
}

interface StorageBarProps {
  /** Current storage used in bytes */
  current: number
  /** Maximum storage in bytes */
  max: number
}

/**
 * A specialized usage bar for displaying storage usage.
 * Automatically formats values as bytes (KB, MB, GB).
 *
 * @example
 * <StorageBar current={1024 * 1024} max={10 * 1024 * 1024} />
 */
export function StorageBar({ current, max }: StorageBarProps) {
  return (
    <UsageBar
      current={current}
      max={max}
      label="Storage"
      icon={Database}
      formatValue={formatBytes}
    />
  )
}
